import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  AcsElement,
  AddMode,
  BankQuestion,
  BuiltProQuiz,
  ExistingQuiz,
  ExistingQuizGroup,
  GeneratedQuestion,
  ProCourse,
  ProModule,
  ProDraftRow,
  QuestionExclusion,
  QuizNameItem,
  QuizFlowQuestion
} from '../../models/quiz-builder-pro.model';
import { DraftAnswer, DraftQuestionType } from '../../models/quiz-builder.model';
import { QuizBuilderProService } from '../../services/quiz-builder-pro.service';
import { blankAnswers, proxyError, QUESTION_TYPES, questionProblem, toggleCorrect } from '../quiz-builder-page/quiz-builder.util';
import { confidenceLabel, loadJson, PRO_QUIZZES_KEY, TYPE_LABELS, weakAreas } from './quiz-builder-pro.util';

type Step = 'setup' | 'build' | 'done';

/** A persisted course quiz in the Quiz Bank; adding copies its questions into the draft and never writes the quiz. */
interface QuizBankRow {
  quiz: ExistingQuiz;
  /** Usable questions in quiz order, once loaded. */
  questions: BankQuestion[] | null;
  loading: boolean;
  error: boolean;
  /** Result of the last add, or the load error. */
  message: string;
  label: string;
  icon: string;
  /** Every loaded question is already in the draft. */
  complete: boolean;
  disabled: boolean;
  request: Subscription | null;
}

/** What the question preview dialog shows, for either a generated or a bank question. */
interface QuestionPreview {
  title: string;
  type: DraftQuestionType;
  meta: string;
  scenario: boolean;
  question: string;
  answers: DraftAnswer[];
  explanation: string;
  byline: string;
  /** Writes an edit back to the source row and returns the new byline. */
  apply: (question: string, answers: DraftAnswer[], explanation: string) => string;
}

/**
 * Prototype: Quiz Builder Pro. Pick a course and optional module, then select or generate questions before naming the quiz.
 * AI questions target the course's most-missed ACS areas. Built quizzes are saved to localStorage only.
 */
@Component({
  selector: 'app-quiz-builder-pro-page',
  templateUrl: './quiz-builder-pro-page.component.html',
  styleUrls: ['./quiz-builder-pro-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuizBuilderProPageComponent implements OnInit, OnDestroy {
  loading = true;
  loadError = '';
  courses: ProCourse[] = [];
  /** Course picker groups: default school courses first, then the rest. */
  courseGroups: { label: string; isDefault: boolean; items: ProCourse[] }[] = [];

  step: Step = 'setup';
  name = '';
  nameLoading = false;
  nameError = '';
  suggestedName = '';
  nameSuggestionLabel = 'Suggest with AI';
  namingVisible = false;
  canCreate = false;
  private nameItems: QuizNameItem[] = [];
  private nameContext = '';
  private nameEdited = false;
  private nameRequest: Subscription;
  courseId: number | null = null;
  moduleId: number | null = null;
  addTab: AddMode = 'bank';
  draft: ProDraftRow[] = [];
  courseChangeHint = '';
  generateLabel = 'Generate 6 questions';
  moduleOptions: ProModule[] = [];
  courseName = '';
  title = 'Build a quiz';
  setupReady = false;

  // Optional existing CAP quiz whose questions seed the draft on Continue.
  existingQuizId: number | null = null;
  quizGroups: ExistingQuizGroup[] = [];
  quizCount = 0;
  quizPickerEnabled = false;
  quizPlaceholder = 'Select a course first';
  quizHint = '';
  quizError = '';
  seeding = false;
  private courseQuizzes: ExistingQuiz[] = [];
  quizListLoading = false;
  private quizListRequest: Subscription;
  private seedRequest: Subscription;
  /** The quiz whose questions are already in the draft; Continue doesn't add them twice. */
  private seededQuizId: number | null = null;

  // AI step
  acsLoading = false;
  acsNote: { icon: string; title: string; message: string } | null = null;
  weak: (AcsElement & { picked: boolean })[] = [];
  codeOptions: { value: string; label: string }[] = [];
  codePicked: string[] = [];
  count = 6;
  generating = false;
  /** One placeholder row per question being generated. */
  skeletonRows: number[] = [];
  /** Placeholder cards while the AI tab loads ACS targets. */
  readonly acsSkeletonRows = [0, 1, 2, 3, 4, 5];
  /** Placeholder rows while the Quiz Bank loads; enough to fill the card. */
  readonly bankSkeletonRows = [0, 1, 2, 3, 4, 5];
  /** 5×5 dot-matrix loader cells (Origin Wave animation lives in the component SCSS). */
  readonly ditherDots = Array.from({ length: 25 }, (_, i) => i);
  aiError = '';
  generationNote = '';
  unresolvedDuplicateCount = 0;
  duplicateReview: ProDraftRow | null = null;
  duplicateExplanation = '';
  duplicateMatchExplanation = '';
  @ViewChild('duplicateDialog', { static: true }) private readonly duplicateDialog: ElementRef<HTMLDialogElement>;

  preview: QuestionPreview | null = null;
  readonly answerLetters = 'ABCDEFGH';
  readonly typeLabels = TYPE_LABELS;
  editing = false;
  draftQuestion = '';
  draftAnswers: DraftAnswer[] = [];
  draftExplanation = '';
  editError = '';
  /** An AI explanation is streaming into the preview (or the edit form). */
  explaining = false;
  explainError = '';
  private explainRequest: Subscription | null = null;
  /** Index of the answer being dragged in the edit form. */
  dragIndex: number | null = null;
  @ViewChild('previewDialog', { static: true }) private readonly previewDialog: ElementRef<HTMLDialogElement>;
  canGenerate = false;

  // Included question order and full student preview.

  @ViewChild('draftList', { static: false }) private readonly draftList: ElementRef<HTMLUListElement>;
  private questionAnimations: Animation[] = [];
  flowQuestions: QuizFlowQuestion[] = [];
  flowQuestion: QuizFlowQuestion | null = null;
  flowAnswers: { text: string; selected: boolean; correct: boolean }[] = [];
  /** The current question's answer was checked: answers lock and the result and explanation show. */
  flowChecked = false;
  flowRight = false;
  flowSelected = false;
  flowCorrectCount = 0;
  flowIndex = 0;
  flowFinished = false;
  flowAnsweredCount = 0;
  flowTitle = '';
  private flowSelections: number[][] = [];
  private flowCheckedAt: boolean[] = [];
  @ViewChild('flowDialog', { static: true }) private readonly flowDialog: ElementRef<HTMLDialogElement>;

  // Quiz Bank: the course's persisted quizzes, from the same list as the setup picker.
  bankSearch = '';
  /** Defaults to the quiz's module. */
  bankModuleId: number | null = null;
  quizBankRows: QuizBankRow[] = [];
  quizBankShownLabel = '';
  quizBankEmptyLabel = '';
  quizListError = '';
  private quizBank: QuizBankRow[] = [];

  readonly questionTypes = QUESTION_TYPES;
  quickAddOpen = false;
  qaType: DraftQuestionType = 'MC';
  qaQuestion = '';
  qaAnswers: DraftAnswer[] = blankAnswers('MC');
  qaProblem = questionProblem('MC', '', this.qaAnswers);
  /** Multiple answer can grow past the default four, up to the 6 that questionProblem allows. */
  readonly maxAnswers = 6;

  createLabel = 'Create quiz';
  created: { quiz: BuiltProQuiz; questions: { id: number; text: string; tag: string }[] } | null = null;

  private modules: ProModule[] = [];
  private elements: AcsElement[] = [];
  private generationRequest: Subscription;
  private acsRequest: Subscription;
  private acsCourseId: number | null = null;
  private saved: BuiltProQuiz[] = loadJson<BuiltProQuiz[]>(PRO_QUIZZES_KEY, []);
  private nextId = -1;
  private readonly subs = new Subscription();

  constructor(
    private readonly service: QuizBuilderProService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.nextId = this.lowestSavedId() - 1;
    this.subs.add(this.service.courses().subscribe(data => {
      this.courses = data.courses;
      this.courseGroups = [
        { label: 'Default courses', isDefault: true, items: data.courses.filter(c => c.isDefault) },
        { label: 'Other courses', isDefault: false, items: data.courses.filter(c => !c.isDefault) }
      ].filter(g => g.items.length);
      this.modules = data.modules;
      this.loading = false;
      this.cdr.markForCheck();
    }, () => {
      this.loading = false;
      this.loadError = 'Could not load courses. Check that you are signed in and the API is reachable.';
      this.cdr.markForCheck();
    }));
  }

  ngOnDestroy(): void {
    this.stopExplanation();
    this.cancelQuestionAnimations();
    this.subs.unsubscribe();
  }

  /** "Build another quiz": back to an empty first step. */
  reset(): void {
    this.step = 'setup';
    this.title = 'Build a quiz';
    this.cancelNameSuggestion();
    this.name = '';
    this.nameEdited = false;
    this.courseId = null;
    this.moduleId = null;
    this.cancelGeneration();
    this.cancelSourceLoads();
    this.resetExistingQuizzes();
    this.addTab = 'bank';
    this.courseChangeHint = '';
    this.moduleOptions = [];
    this.draft = [];
    this.codePicked = [];
    this.weak = [];
    this.aiError = '';
    this.generationNote = '';
    this.bankSearch = '';
    this.closeQuickAdd();
    this.created = null;
    this.refreshSetup();
    this.refreshCreate();
  }

  onCourseChanged(): void {
    this.moduleId = null;
    this.moduleOptions = this.modules.filter(m => m.courseId === this.courseId);
    this.courseName = (this.courses.find(c => c.id === this.courseId) || { name: '' }).name;
    // Cancel pending work before clearing the draft so results from the old course cannot append later.
    this.courseChangeHint = this.draft.length ? `Changing the course removed the ${this.draft.length} questions in this quiz.` : '';
    this.cancelGeneration();
    this.cancelSourceLoads();
    this.draft = [];
    this.codePicked = [];
    this.closeQuickAdd();
    this.resetExistingQuizzes();
    this.loadCourseQuizzes();
    this.onModuleChanged();
    this.refreshCreate();
  }

  onModuleChanged(): void {
    this.cancelNameSuggestion();
    this.name = this.nameEdited ? this.name : this.defaultName();
    this.nameSuggestionLabel = 'Suggest with AI';
    this.bankModuleId = this.moduleId;
    this.applyQuizBankFilter();
    this.refreshQuizOptions();
    this.refreshSetup();
  }

  onExistingQuizChanged(): void {
    this.cancelSeed();
    this.refreshQuizOptions();
  }

  onNameChanged(): void {
    this.nameEdited = true;
    this.refreshCreate();
  }

  useSuggestedName(): void {
    this.name = this.suggestedName;
    this.nameEdited = false;
    this.refreshCreate();
  }

  suggestName(): void {
    const module = this.moduleOptions.find(m => m.id === this.moduleId);
    if (!this.namingVisible || this.nameLoading) {
      return;
    }
    this.nameLoading = true;
    this.nameError = '';
    this.nameRequest = this.service.suggestName(this.courseName, module ? module.label : '', this.draft.every(row => row.source === 'ai') ? 'ai' : 'bank',
      this.nameItems.length, this.nameItems.slice(0, 15)).subscribe(name => {
      this.suggestedName = name;
      this.nameSuggestionLabel = 'Suggest again';
      if (!this.nameEdited) {
        this.name = name;
      }
      this.nameLoading = false;
      this.refreshCreate();
      this.cdr.markForCheck();
    }, () => {
      this.nameLoading = false;
      this.nameError = 'Couldn’t get a suggestion. Keep this name or enter your own.';
      this.cdr.markForCheck();
    });
    this.subs.add(this.nameRequest);
  }

  private cancelNameSuggestion(): void {
    if (this.nameRequest) {
      this.nameRequest.unsubscribe();
    }
    this.nameLoading = false;
    this.nameError = '';
    this.suggestedName = '';
  }

  refreshSetup(): void {
    this.setupReady = this.courseId != null;
  }

  next(): void {
    if (!this.setupReady || this.seeding) {
      return;
    }
    const quiz = this.courseQuizzes.find(q => q.id === this.existingQuizId);
    if (quiz && quiz.id !== this.seededQuizId) {
      this.seedFromQuiz(quiz);
      return;
    }
    this.cancelNameSuggestion();
    this.step = 'build';
    const module = this.moduleOptions.find(m => m.id === this.moduleId);
    this.title = `Build quiz — ${this.courseName} · ${module ? module.label : 'No module'}`;
    this.selectAddTab(this.addTab);
    this.refreshCreate();
  }

  selectAddTab(tab: AddMode): void {
    this.addTab = tab;
    if (tab === 'bank') {
      this.applyQuizBankFilter();
    } else if (this.acsCourseId !== this.courseId && !this.acsLoading) {
      this.loadAcs();
    }
  }

  onAddTabKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.selectAddTab(this.addTab === 'bank' ? 'ai' : 'bank');
      this.cdr.detectChanges();
      document.getElementById(`qbw-tab-${this.addTab}`).focus();
    }
  }

  back(): void {
    this.cancelNameSuggestion();
    this.step = 'setup';
    this.title = 'Build a quiz';
    this.aiError = '';
  }

  // ---- Existing quiz ----

  /** Loads the quiz's questions, appends any not already in the draft, then continues to the build step. */
  private seedFromQuiz(quiz: ExistingQuiz): void {
    this.seeding = true;
    this.quizError = '';
    this.seedRequest = this.service.quizQuestions(quiz.id).subscribe(({ questions, skipped }) => {
      this.seeding = false;
      const added = this.appendQuestions(questions);
      const row = this.quizBank.find(r => r.quiz.id === quiz.id);
      if (row) { row.questions = questions; }
      this.seededQuizId = quiz.id;
      if (!this.nameEdited) { this.name = quiz.name; }
      const notes = [`Started from ${quiz.name}: added ${added.length} question${added.length === 1 ? '' : 's'}.`];
      if (skipped) { notes.push(`${skipped} retired question${skipped === 1 ? ' was' : 's were'} left out.`); }
      this.courseChangeHint = '';
      this.generationNote = notes.join(' ');
      this.next();
      this.cdr.markForCheck();
    }, () => {
      this.seeding = false;
      this.quizError = 'Could not load this quiz’s questions. Try again, or clear it to start from scratch.';
      this.cdr.markForCheck();
    });
    this.subs.add(this.seedRequest);
  }

  private loadCourseQuizzes(): void {
    if (this.courseId == null) { return; }
    if (this.quizListRequest) { this.quizListRequest.unsubscribe(); }
    this.quizListLoading = true;
    this.quizListError = '';
    this.quizListRequest = this.service.courseQuizzes(this.courseId).subscribe(quizzes => {
      this.quizListLoading = false;
      this.courseQuizzes = quizzes;
      this.quizBank = quizzes.map(quiz => ({ quiz, questions: null, loading: false, error: false, message: '', label: '', icon: '',
        complete: false, disabled: false, request: null }));
      this.refreshQuizRows();
      this.applyQuizBankFilter();
      this.refreshQuizOptions();
      this.cdr.markForCheck();
    }, () => {
      this.quizListLoading = false;
      this.quizError = 'Could not load this course’s quizzes. You can still start from scratch.';
      this.quizListError = 'Could not load this course’s quizzes.';
      this.refreshQuizOptions();
      this.cdr.markForCheck();
    });
    this.subs.add(this.quizListRequest);
  }

  /** Cancels the course's quiz list and any pending seed so a late response can't apply to another course. */
  private resetExistingQuizzes(): void {
    if (this.quizListRequest) { this.quizListRequest.unsubscribe(); }
    this.cancelSeed();
    this.quizBank.forEach(row => row.request && row.request.unsubscribe());
    this.quizBank = [];
    this.quizBankRows = [];
    this.quizListError = '';
    this.quizListLoading = false;
    this.courseQuizzes = [];
    this.existingQuizId = null;
    this.seededQuizId = null;
    this.quizError = '';
    this.refreshQuizOptions();
  }

  private cancelSeed(): void {
    if (this.seedRequest) { this.seedRequest.unsubscribe(); }
    this.seeding = false;
    this.quizError = '';
  }

  /** Quizzes of the selected module only, or every module's grouped in module order; a selection outside them is cleared. */
  private refreshQuizOptions(): void {
    const quizzes = this.moduleId == null ? this.courseQuizzes : this.courseQuizzes.filter(q => q.moduleIds.includes(this.moduleId));
    if (this.existingQuizId != null && !quizzes.some(q => q.id === this.existingQuizId)) {
      this.existingQuizId = null;
      this.cancelSeed();
    }
    const modules = this.moduleId == null ? this.moduleOptions : this.moduleOptions.filter(m => m.id === this.moduleId);
    this.quizGroups = modules.map(m => ({ label: m.label, items: quizzes.filter(q => (this.moduleId ?? q.moduleIds[0]) === m.id) }))
      .filter(g => g.items.length);
    this.quizCount = quizzes.length;
    this.quizPickerEnabled = this.courseId != null && !this.quizListLoading && quizzes.length > 0;
    this.quizPlaceholder = this.courseId == null ? 'Select a course first'
      : this.quizListLoading ? 'Loading quizzes…'
      : quizzes.length ? 'Start from scratch'
      : this.moduleId == null ? 'No quizzes in this course' : 'No quizzes in this module';
    const selected = quizzes.find(q => q.id === this.existingQuizId);
    const copied = selected && (selected.questionCount === 1 ? 'Its question is' : `Its ${selected.questionCount} questions are`);
    this.quizHint = selected ? `${copied} copied into this quiz. The original quiz isn’t changed.`
      : this.quizPickerEnabled ? 'Copy questions from a quiz already in this course, or leave empty to start from scratch.' : '';
  }

  // ---- AI step ----

  togglePick(element: AcsElement & { picked: boolean }): void {
    element.picked = !element.picked;
    this.refreshAi();
  }

  removeCode(code: string): void {
    this.codePicked = this.codePicked.filter(c => c !== code);
    this.refreshAi();
  }

  changeCount(delta: number): void {
    this.count = Math.min(20, Math.max(1, this.count + delta));
    this.refreshGenerateLabel();
  }

  generate(): void {
    const codes = new Set([...this.weak.filter(w => w.picked).map(w => w.code), ...this.codePicked]);
    const targets = this.elements.filter(e => codes.has(e.code));
    if (!targets.length || this.generating) {
      return;
    }
    this.generating = true;
    this.skeletonRows = Array.from({ length: this.count }, (_, i) => i);
    this.aiError = '';
    this.generationNote = '';
    // Reserve ids before the request; Quick add can run while AI is generating.
    const firstId = this.nextId;
    this.nextId -= this.count;
    const exclusions: QuestionExclusion[] = this.draft.map(row => ({ id: row.q.id, source: 'draft', text: row.text,
      type: row.q.type, answers: row.q.answers, moduleId: this.moduleId }));
    for (const quiz of this.saved.filter(q => q.courseId === this.courseId)) {
      exclusions.push(...quiz.bankQuestions.map(q => ({ id: q.id, source: 'saved' as const, text: q.text,
        type: q.type, answers: q.answers, moduleId: quiz.moduleId })));
      exclusions.push(...quiz.generated.map(q => ({ id: q.id, source: 'saved' as const, text: q.question,
        type: q.type, answers: q.answers, moduleId: quiz.moduleId })));
    }
    const requestedCount = this.count;
    this.generationRequest = this.service.generate(this.courseName, targets, requestedCount, firstId,
      this.courseId, this.moduleId, exclusions).subscribe(result => {
      const texts = new Set(this.draft.map(row => row.text.trim().toLowerCase()));
      let added = 0;
      for (const q of result.questions) {
        // ponytail: exact-text dedupe; avoid adding a repeated draft when generating again.
        const text = q.question.trim().toLowerCase();
        if (!texts.has(text)) {
          this.draft = [...this.draft, { source: 'ai', q, text: q.question, tag: `AI · ${q.acsCode}`,
            typeLabel: TYPE_LABELS[q.type], confidence: confidenceLabel(q.confidence), similarTo: q.similarTo }];
          texts.add(text);
          added++;
        }
      }
      const notes: string[] = [];
      if (result.skipped) { notes.push(`${result.skipped} duplicate${result.skipped === 1 ? '' : 's'} skipped.`); }
      if (added < requestedCount) { notes.push(`Added ${added} of ${requestedCount} questions.`); }
      if (!result.similarityChecked) { notes.push('Exact duplicates checked; AI similarity review is unavailable.'); }
      this.generationNote = notes.join(' ');
      this.generating = false;
      this.refreshCreate();
      this.cdr.markForCheck();
    }, err => {
      this.aiError = proxyError(err);
      this.generating = false;
      this.cdr.markForCheck();
    });
    this.subs.add(this.generationRequest);
  }

  reviewDuplicate(row: ProDraftRow): void {
    this.duplicateReview = row;
    this.duplicateExplanation = row.q.explanation || '';
    const id = row.similarTo.id;
    const inDraft = this.draft.find(item => item !== row && item.q.id === id);
    this.duplicateMatchExplanation = inDraft ? inDraft.q.explanation || '' : '';
    if (!inDraft && row.similarTo.source === 'bank' && this.courseId != null) {
      // Cached by generation's duplicate check, so this resolves immediately.
      this.service.bankQuestions(this.courseId).pipe(take(1)).subscribe(bank => {
        const match = bank.find(q => q.id === id);
        this.duplicateMatchExplanation = match ? match.explanation || '' : '';
        this.cdr.markForCheck();
      });
    }
    this.cdr.detectChanges();
    this.duplicateDialog.nativeElement.showModal();
  }

  closeDuplicateReview(): void {
    this.duplicateDialog.nativeElement.close();
    this.duplicateReview = null;
  }

  keepDuplicate(): void {
    const row = this.duplicateReview;
    if (row) {
      row.similarTo = undefined;
      if (row.source === 'ai') { row.q = { ...row.q, similarTo: undefined }; }
    }
    this.refreshCreate();
    this.closeDuplicateReview();
  }

  useDuplicate(): void {
    const row = this.duplicateReview;
    if (!row || !row.similarTo) { return; }
    const match = row.similarTo;
    const index = this.draft.indexOf(row);
    if (index < 0) { this.closeDuplicateReview(); return; }
    if (this.draft.some(q => q !== row && q.q.id === match.id)) {
      this.draft = this.draft.filter(q => q !== row);
    } else {
      const q: BankQuestion = { id: match.id, name: '', text: match.text, moduleId: match.moduleId,
        type: match.type, typeLabel: TYPE_LABELS[match.type], answers: match.answers.map(a => ({ ...a })),
        explanation: this.duplicateMatchExplanation };
      this.draft = this.draft.map((current, i): ProDraftRow => i === index ? { source: match.source === 'bank' ? 'bank' : 'new',
        q, text: q.text, typeLabel: q.typeLabel, tag: match.source === 'bank' ? 'Bank' : 'Existing', confidence: '' } : current);
    }
    this.refreshCreate();
    this.closeDuplicateReview();
  }

  private cancelSourceLoads(): void {
    if (this.acsRequest) { this.acsRequest.unsubscribe(); }
    this.acsLoading = false;
    this.acsCourseId = null;
    this.weak = [];
    this.elements = [];
    this.codeOptions = [];
    this.refreshAi();
  }

  private cancelGeneration(): void {
    if (this.generationRequest) { this.generationRequest.unsubscribe(); }
    this.generating = false;
    this.aiError = '';
    this.generationNote = '';
  }

  previewDraft(row: ProDraftRow): void {
    if (row.source === 'ai') {
      this.previewGenerated(row);
    } else {
      this.openPreview({ title: row.q.name, type: row.q.type, meta: '', scenario: false, question: row.text,
        answers: row.q.answers, explanation: row.q.explanation || '', byline: '', apply: (question, answers, explanation) => {
          row.q = { ...row.q, text: question, answers, explanation };
          row.text = question;
          this.refreshCreate();
          return 'Edited for this quiz';
        } });
    }
  }

  previewGenerated(row: ProDraftRow & { source: 'ai' }): void {
    const byline = () => `Written by AI${row.confidence ? ` · checked by TypeSafe Jev (${row.confidence})` : ''}`;
    this.openPreview({
      title: row.q.acsCode,
      type: row.q.type,
      meta: row.q.reference,
      scenario: row.q.scenario,
      question: row.q.question,
      answers: row.q.answers,
      explanation: row.q.explanation || '',
      byline: byline(),
      apply: (question, answers, explanation) => {
        // Jev scored the original wording, so its confidence no longer applies; an explanation-only change keeps it.
        const reworded = question !== row.q.question || JSON.stringify(answers) !== JSON.stringify(row.q.answers);
        if (reworded) { row.confidence = ''; }
        row.q = { ...row.q, question, answers, explanation };
        row.text = question;
        this.refreshCreate();
        return reworded ? 'Written by AI · edited' : byline();
      }
    });
  }

  startEdit(): void {
    this.draftQuestion = this.preview.question;
    this.draftAnswers = this.preview.answers.map(a => ({ ...a }));
    this.draftExplanation = this.preview.explanation;
    this.editError = '';
    this.editing = true;
    // The Edit button just left the DOM; put focus in the form rather than on the dialog.
    this.cdr.detectChanges();
    this.previewDialog.nativeElement.querySelector('textarea').focus();
  }

  /** Exclusive for True/False and Multiple choice, toggled for Multiple answer. */
  toggleDraftCorrect(index: number): void {
    this.draftAnswers = toggleCorrect(this.preview.type, this.draftAnswers, index);
  }

  onAnswerDragStart(event: DragEvent, index: number, row: HTMLElement): void {
    this.dragIndex = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', '');
    event.dataTransfer.setDragImage(row, 16, row.offsetHeight / 2);
  }

  /** Reorders live as the pointer crosses rows, so the drop position is always what you see. */
  onAnswerDragOver(event: DragEvent, index: number): void {
    if (this.dragIndex == null) {
      return;
    }
    event.preventDefault();
    if (index !== this.dragIndex) {
      this.moveAnswer(this.dragIndex, index);
      this.dragIndex = index;
    }
  }

  onAnswerDragEnd(): void {
    this.dragIndex = null;
  }

  /** Keyboard reorder on the grip: ArrowUp / ArrowDown. */
  onGripKeydown(event: KeyboardEvent, index: number): void {
    const to = index + (event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0);
    if (to === index || to < 0 || to >= this.draftAnswers.length) {
      return;
    }
    event.preventDefault();
    this.moveAnswer(index, to);
    // Moving the row's DOM node drops focus; put it back on the same answer's grip.
    this.cdr.detectChanges();
    this.previewDialog.nativeElement.querySelectorAll<HTMLElement>('.qbw-dialog__grip')[to].focus();
  }

  private moveAnswer(from: number, to: number): void {
    const answers = [...this.draftAnswers];
    answers.splice(to, 0, ...answers.splice(from, 1));
    this.draftAnswers = answers;
  }

  cancelEdit(): void {
    this.stopExplanation();
    this.editing = false;
    this.editError = '';
  }

  saveEdit(): void {
    const question = this.draftQuestion.trim();
    const answers = this.draftAnswers.map(a => ({ ...a, text: a.text.trim() }));
    this.editError = questionProblem(this.preview.type, question, answers);
    if (this.editError) {
      return;
    }
    const explanation = this.draftExplanation.trim();
    const byline = this.preview.apply(question, answers, explanation);
    this.preview = { ...this.preview, question, answers, explanation, byline };
    this.cancelEdit();
  }

  /**
   * Streams an AI explanation for the question as shown (the edit form's wording while editing).
   * In the read view the finished text is saved to the quiz; in the edit form it waits for Save changes.
   */
  writeExplanation(): void {
    this.stopExplanation();
    const editing = this.editing;
    const preview = this.preview;
    const before = editing ? this.draftExplanation : preview.explanation;
    const show = (text: string) => {
      if (editing) { this.draftExplanation = text; } else { this.preview = { ...this.preview, explanation: text }; }
      this.cdr.markForCheck();
    };
    this.explaining = true;
    this.explainError = '';
    this.explainRequest = this.service.explain(preview.type, editing ? this.draftQuestion.trim() : preview.question,
      editing ? this.draftAnswers : preview.answers, preview.meta).subscribe({
      next: show,
      error: err => {
        this.explaining = false;
        this.explainError = proxyError(err);
        show(before);
      },
      complete: () => {
        this.explaining = false;
        const explanation = (editing ? this.draftExplanation : this.preview.explanation).trim();
        if (!explanation) {
          this.explainError = 'The AI returned no explanation. Try again.';
          show(before);
        } else if (editing) {
          show(explanation);
        } else {
          this.preview = { ...this.preview, explanation, byline: preview.apply(preview.question, preview.answers, explanation) };
          this.cdr.markForCheck();
        }
      }
    });
  }

  /** Stops a stream without saving it; the open preview is rebuilt from its row next time. */
  stopExplanation(): void {
    if (this.explainRequest) { this.explainRequest.unsubscribe(); }
    this.explainRequest = null;
    this.explaining = false;
    this.explainError = '';
  }

  closePreview(): void {
    this.previewDialog.nativeElement.close();
  }

  /** Clicks on the dialog element itself land on the backdrop; the panel inside stops them. */
  onPreviewBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.editing) {
      this.closePreview();
    }
  }

  /** Esc while editing discards the edit instead of closing the dialog. */
  onPreviewCancel(event: Event): void {
    if (this.editing) {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  private openPreview(preview: QuestionPreview): void {
    this.preview = preview;
    this.cancelEdit();
    this.cdr.detectChanges();
    this.previewDialog.nativeElement.showModal();
  }

  refreshAi(): void {
    this.canGenerate = this.weak.some(w => w.picked) || this.codePicked.length > 0;
  }

  // ---- Quiz Bank ----

  applyQuizBankFilter(): void {
    const search = this.bankSearch.trim().toLowerCase();
    this.quizBankRows = this.quizBank.filter(r =>
      (this.bankModuleId == null || r.quiz.moduleIds.includes(this.bankModuleId)) &&
      (!search || `${r.quiz.name} ${r.quiz.meta}`.toLowerCase().includes(search)));
    const n = this.quizBankRows.length;
    this.quizBankShownLabel = `${n} quiz${n === 1 ? '' : 'zes'}`;
    this.quizBankEmptyLabel = this.quizBank.length ? 'No quizzes match.'
      : 'This course has no quizzes yet. Generate with AI or quick add instead.';
  }

  retryQuizList(): void {
    this.quizError = '';
    this.loadCourseQuizzes();
    this.refreshQuizOptions();
  }

  /** Copies the quiz's questions into the draft in quiz order; repeats add only the ones not already there. */
  addQuizQuestions(row: QuizBankRow): void {
    if (row.disabled) { return; }
    if (row.questions) {
      this.showQuizAdd(row, this.appendQuestions(row.questions).length, 0);
      return;
    }
    row.loading = true;
    row.error = false;
    row.message = '';
    this.refreshQuizRows();
    row.request = this.service.quizQuestions(row.quiz.id).subscribe(({ questions, skipped }) => {
      row.loading = false;
      row.questions = questions;
      this.showQuizAdd(row, this.appendQuestions(questions).length, skipped);
      this.cdr.markForCheck();
    }, () => {
      row.loading = false;
      row.error = true;
      row.message = 'Could not load this quiz’s questions.';
      this.refreshQuizRows();
      this.cdr.markForCheck();
    });
    this.subs.add(row.request);
  }

  private showQuizAdd(row: QuizBankRow, added: number, skipped: number): void {
    const notes = [added ? `Added ${added} question${added === 1 ? '' : 's'}.`
      : row.questions.length ? 'Its questions are already in this quiz.' : 'This quiz has no active questions.'];
    if (skipped) { notes.push(`${skipped} retired question${skipped === 1 ? ' was' : 's were'} left out.`); }
    row.message = notes.join(' ');
    this.refreshCreate();
  }

  /** Appends questions not already in the draft, in their given order; returns the ones added. */
  private appendQuestions(questions: BankQuestion[]): BankQuestion[] {
    const ids = new Set(this.draft.map(row => row.q.id));
    const added = questions.filter(q => !ids.has(q.id));
    this.draft = [...this.draft, ...added.map((q): ProDraftRow => ({ source: 'bank', q, text: q.text, tag: '',
      typeLabel: q.typeLabel, confidence: '' }))];
    return added;
  }

  private refreshQuizRows(): void {
    const ids = new Set(this.draft.map(row => row.q.id));
    for (const row of this.quizBank) {
      const missing = row.questions ? row.questions.filter(q => !ids.has(q.id)).length : -1;
      row.complete = !row.loading && missing === 0 && row.questions.length > 0;
      row.disabled = row.loading || missing === 0;
      row.label = row.loading ? 'Adding…' : row.error ? 'Retry' : missing < 0 ? 'Add questions'
        : missing ? `Add ${missing} missing` : row.questions.length ? 'All in quiz' : 'No questions';
      row.icon = row.loading ? 'fa-spinner fa-spin' : row.error ? 'fa-refresh' : row.complete ? 'fa-check' : 'fa-plus';
    }
  }

  openQuickAdd(): void {
    this.quickAddOpen = true;
    this.cdr.detectChanges();
    (document.getElementById('qbw-qa-question') as HTMLTextAreaElement).focus();
  }

  closeQuickAdd(): void {
    this.quickAddOpen = false;
    this.qaQuestion = '';
    this.qaAnswers = blankAnswers(this.qaType);
    this.refreshQuickAdd();
  }

  /** Choice types keep typed answers when switching between them; True/False always starts from its fixed pair. */
  setQuickAddType(type: DraftQuestionType): void {
    const keepText = type !== 'TF' && this.qaType !== 'TF';
    this.qaType = type;
    // Multiple choice stays at four answers; extra Multiple answer rows are dropped when switching to it.
    this.qaAnswers = keepText
      ? this.qaAnswers.slice(0, type === 'MC' ? 4 : this.maxAnswers).map(a => ({ ...a, correct: false }))
      : blankAnswers(type);
    this.refreshQuickAdd();
  }

  toggleQuickAddCorrect(index: number): void {
    this.qaAnswers = toggleCorrect(this.qaType, this.qaAnswers, index);
    this.refreshQuickAdd();
  }

  addQuickAnswer(): void {
    if (this.qaAnswers.length >= this.maxAnswers) {
      return;
    }
    this.qaAnswers = [...this.qaAnswers, { text: '', correct: false }];
    this.refreshQuickAdd();
    this.cdr.detectChanges();
    const inputs = document.querySelectorAll<HTMLInputElement>('.qbw-qa__answer input');
    inputs[inputs.length - 1].focus();
  }

  removeQuickAnswer(index: number): void {
    this.qaAnswers = this.qaAnswers.filter((_, i) => i !== index);
    this.refreshQuickAdd();
  }

  refreshQuickAdd(): void {
    this.qaProblem = questionProblem(this.qaType, this.qaQuestion, this.qaAnswers);
  }

  /** Adds the question (selected) and clears the form for the next one. */
  addQuickQuestion(): void {
    if (this.qaProblem) {
      return;
    }
    const q: BankQuestion = {
      id: this.nextId--,
      name: 'New question',
      text: this.qaQuestion.trim(),
      moduleId: this.bankModuleId ?? this.moduleId,
      type: this.qaType,
      typeLabel: TYPE_LABELS[this.qaType],
      answers: this.qaAnswers.map(a => ({ text: a.text.trim(), correct: a.correct }))
    };
    this.draft = [...this.draft, { source: 'new', q, text: q.text, tag: 'New', typeLabel: q.typeLabel, confidence: '' }];
    this.qaQuestion = '';
    this.qaAnswers = blankAnswers(this.qaType);
    this.refreshQuickAdd();
    this.refreshCreate();
    (document.getElementById('qbw-qa-question') as HTMLTextAreaElement).focus();
  }

  removeDraft(row: ProDraftRow): void {
    this.draft = this.draft.filter(item => item.q.id !== row.q.id);
    this.refreshCreate();
  }

  moveQuestion(from: number, to: number, animate = true): void {
    if (from === to || from < 0 || to < 0 || from >= this.draft.length || to >= this.draft.length) {
      return;
    }
    const list = this.draftList && this.draftList.nativeElement;
    const shouldAnimate = animate && list && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Measure the current visual position before cancelling an interrupted move.
    const before = new Map<number, number>();
    if (shouldAnimate) {
      list.querySelectorAll<HTMLElement>('[data-question-id]').forEach(element => {
        before.set(Number(element.dataset.questionId), element.getBoundingClientRect().top);
      });
    }
    this.cancelQuestionAnimations();
    const reordered = [...this.draft];
    const [row] = reordered.splice(from, 1);
    reordered.splice(to, 0, row);
    this.draft = reordered;
    this.refreshCreate();
    this.cdr.detectChanges();
    if (shouldAnimate) {
      this.animateQuestionPositions(list, before);
    }
  }

  private animateQuestionPositions(list: HTMLUListElement, before: Map<number, number>): void {
    const styles = window.getComputedStyle(list);
    const durationToken = styles.getPropertyValue('--duration-normal').trim();
    const duration = parseFloat(durationToken) * (durationToken.endsWith('ms') ? 1 : 1000) || 200;
    const easing = styles.getPropertyValue('--easing-in-out').trim() || 'ease-in-out';
    // FLIP: batch geometry reads, then animate only transforms on rows that actually moved.
    const moves = Array.from(list.querySelectorAll<HTMLElement>('[data-question-id]')).map(element => ({
      element, offset: (before.get(Number(element.dataset.questionId)) ?? element.getBoundingClientRect().top)
        - element.getBoundingClientRect().top
    }));
    moves.forEach(({ element, offset }) => {
      if (!offset || !element.animate) { return; }
      const animation = element.animate([
        { transform: `translateY(${offset}px)` }, { transform: 'translateY(0)' }
      ], { duration, easing });
      this.questionAnimations.push(animation);
      animation.onfinish = () => this.questionAnimations = this.questionAnimations.filter(active => active !== animation);
    });
  }

  private cancelQuestionAnimations(): void {
    this.questionAnimations.forEach(animation => animation.cancel());
    this.questionAnimations = [];
  }

  onQuestionDragStarted(): void {
    this.cancelQuestionAnimations();
  }

  dropQuestion(event: CdkDragDrop<ProDraftRow[]>): void {
    if (!event.isPointerOverContainer) { return; }
    const from = this.draft.findIndex(row => row.q.id === event.item.data.q.id);
    // CDK moves the whole row and sorts its neighbors during the gesture; don't animate again after release.
    this.moveQuestion(from, event.currentIndex, false);
  }

  onQuestionOrderKey(event: KeyboardEvent, index: number): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const id = this.draft[index].q.id;
      this.moveQuestion(index, index + (event.key === 'ArrowUp' ? -1 : 1), false);
      this.cdr.detectChanges();
      const handle = document.getElementById(`qbw-order-${id}`);
      handle.focus({ preventScroll: true });
      handle.scrollIntoView({ block: 'nearest' });
    }
  }

  previewQuiz(): void {
    this.flowQuestions = this.draft.map(row => ({ id: row.q.id, text: row.text, type: row.q.type, answers: row.q.answers,
      explanation: row.q.explanation || '' }));
    if (!this.flowQuestions.length) {
      return;
    }
    this.flowTitle = this.name.trim() || this.defaultName();
    this.restartFlow();
    this.cdr.detectChanges();
    this.flowDialog.nativeElement.showModal();
  }

  restartFlow(): void {
    this.flowSelections = this.flowQuestions.map(() => []);
    this.flowCheckedAt = this.flowQuestions.map(() => false);
    this.flowCorrectCount = 0;
    this.flowIndex = 0;
    this.flowFinished = false;
    this.flowAnsweredCount = 0;
    this.refreshFlow();
  }

  selectFlowAnswer(index: number): void {
    if (this.flowChecked) {
      return;
    }
    const selected = this.flowSelections[this.flowIndex];
    this.flowSelections[this.flowIndex] = this.flowQuestion.type === 'MA'
      ? selected.includes(index) ? selected.filter(i => i !== index) : [...selected, index]
      : [index];
    this.flowAnsweredCount = this.flowSelections.filter(answers => answers.length > 0).length;
    this.refreshFlow();
  }

  checkFlowAnswer(): void {
    this.flowCheckedAt[this.flowIndex] = true;
    this.flowCorrectCount = this.flowQuestions.filter((q, i) => this.flowCheckedAt[i] && this.isFlowRight(i)).length;
    this.refreshFlow();
  }

  /** Right only when exactly the correct answers are selected. */
  private isFlowRight(index: number): boolean {
    const selected = this.flowSelections[index];
    return this.flowQuestions[index].answers.every((a, i) => a.correct === selected.includes(i));
  }

  moveFlow(delta: number): void {
    const to = this.flowIndex + delta;
    if (to >= this.flowQuestions.length) {
      this.flowFinished = true;
    } else if (to >= 0) {
      this.flowIndex = to;
      this.refreshFlow();
    }
    this.cdr.detectChanges();
    this.flowDialog.nativeElement.querySelector<HTMLElement>('.qbw-flow__content').focus();
  }

  closeFlow(): void {
    this.flowDialog.nativeElement.close();
  }

  onFlowBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeFlow();
    }
  }

  private refreshFlow(): void {
    this.flowQuestion = this.flowQuestions[this.flowIndex];
    this.flowAnswers = this.flowQuestion.answers.map((answer, i) => ({
      text: answer.text, selected: this.flowSelections[this.flowIndex].includes(i), correct: answer.correct
    }));
    this.flowChecked = this.flowCheckedAt[this.flowIndex];
    this.flowRight = this.flowChecked && this.isFlowRight(this.flowIndex);
    this.flowSelected = this.flowSelections[this.flowIndex].length > 0;
  }

  // ---- Finish ----

  create(): void {
    const generated: GeneratedQuestion[] = [];
    const bankQuestions: BankQuestion[] = [];
    for (const row of this.draft) {
      if (row.source === 'ai') { generated.push(row.q); } else { bankQuestions.push(row.q); }
    }
    if (!this.canCreate) {
      return;
    }
    this.cancelNameSuggestion();
    this.cancelGeneration();
    const quiz: BuiltProQuiz = {
      id: Math.min(this.nextId, this.lowestSavedId() - 1),
      name: this.name.trim(),
      courseId: this.courseId,
      moduleId: this.moduleId,
      bankQuestions,
      generated,
      order: this.draft.map(row => row.q.id),
      createdAt: new Date().toISOString()
    };
    this.nextId = quiz.id - 1;
    this.saved = [quiz, ...this.saved];
    localStorage.setItem(PRO_QUIZZES_KEY, JSON.stringify(this.saved));
    this.created = {
      quiz,
      questions: this.draft.map(row => ({ id: row.q.id, text: row.text, tag: row.tag }))
    };
    this.step = 'done';
    this.title = 'Quiz created';
  }

  trackById(_: number, row: { q: { id: number } }): number {
    return row.q.id;
  }

  trackByQuiz(_: number, row: QuizBankRow): number {
    return row.quiz.id;
  }

  trackByCode(_: number, item: { code: string }): string {
    return item.code;
  }

  trackByQuestion(_: number, item: { id: number }): number {
    return item.id;
  }

  private defaultName(): string {
    const module = this.moduleOptions.find(m => m.id === this.moduleId);
    return `${module ? module.label : this.courseName} quiz`;
  }

  private refreshCreate(): void {
    this.refreshQuizRows();
    this.nameItems = this.draft.map(row => {
      if (row.source === 'ai') {
        const q = row.q;
        return { text: row.text, code: q.acsCode,
          description: (this.elements.find(e => e.code === q.acsCode) || { description: '' }).description };
      }
      return { text: row.text, name: row.q.name };
    });
    const context = JSON.stringify([this.courseId, this.moduleId, this.nameItems]);
    if (context !== this.nameContext) {
      this.cancelNameSuggestion();
      this.nameContext = context;
    }
    const n = this.nameItems.length;
    this.namingVisible = n > 0;
    this.unresolvedDuplicateCount = this.draft.filter(row => !!row.similarTo).length;
    this.canCreate = this.step === 'build' && this.namingVisible && !!this.name.trim() && this.unresolvedDuplicateCount === 0;
    this.refreshGenerateLabel();
    this.createLabel = n ? `Create quiz with ${n} question${n === 1 ? '' : 's'}` : 'Create quiz';
  }

  private refreshGenerateLabel(): void {
    this.generateLabel = `Generate ${this.count} ${this.draft.some(row => row.source === 'ai') ? 'more' : 'questions'}`;
  }

  private lowestSavedId(): number {
    // Quick-add questions are saved among bankQuestions with negative ids, so count those too.
    const ids = [].concat(...this.saved.map(q => [q.id, ...q.generated.map(g => g.id), ...q.bankQuestions.map(b => b.id)]));
    return Math.min(0, ...ids);
  }

  private loadAcs(): void {
    const course = this.courses.find(c => c.id === this.courseId);
    this.acsNote = null;
    if (!course || course.acsTestId == null) {
      this.elements = [];
      this.weak = [];
      this.codeOptions = [];
      this.acsNote = {
        icon: 'fa-unlink', title: 'No ACS test linked',
        message: `${this.courseName || 'This course'} has no ACS test linked, so there are no ACS codes to target.`
      };
      this.refreshAi();
      return;
    }
    this.acsLoading = true;
    this.acsRequest = this.service.acsElements(course.acsTestId).subscribe(elements => {
      this.acsCourseId = this.courseId;
      this.elements = elements;
      const picked = new Set(this.weak.filter(w => w.picked).map(w => w.code));
      this.weak = weakAreas(elements).map(e => ({ ...e, picked: picked.has(e.code) }));
      this.codeOptions = elements.map(e => ({ value: e.code, label: `${e.code} — ${e.description}` }));
      this.acsNote = this.weak.length ? null : {
        icon: 'fa-bullseye', title: 'No weak areas yet',
        message: 'No FAA written-test misses are recorded for this course. Choose ACS codes below to target instead.'
      };
      this.acsLoading = false;
      this.refreshAi();
      this.cdr.markForCheck();
    }, () => {
      this.acsLoading = false;
      this.acsNote = { icon: 'fa-exclamation-triangle', title: 'ACS codes unavailable', message: 'Could not load ACS codes for this course.' };
      this.cdr.markForCheck();
    });
    this.subs.add(this.acsRequest);
  }
}
